import { backendBaseUrl } from '../config';
import { notifyUnauthorized } from './appAuth';

function targetsBackend(url: string): boolean {
  if (backendBaseUrl && url.startsWith(backendBaseUrl)) return true;
  // Same-origin relative or absolute /api calls.
  if (url.startsWith('/api/')) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

function urlFromInput(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installAppAuthFetchInterceptor(): void {
  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = urlFromInput(input);
    if (!targetsBackend(url)) {
      return original(input, init);
    }

    // WI-04b: the shared X-App-Password header is gone — auth now rides the ambient SSO session
    // cookie (`sso_session`) alone. Preserve any caller-supplied headers.
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));

    // Phase 7 hotfix #4: SWA host (FE) and Container Apps host (API) are
    // CROSS-ORIGIN. The browser only sends the `sso_session` cookie on a
    // cross-origin fetch when credentials are explicitly included. Without
    // this, the session exchange mints the cookie but every SUBSEQUENT API
    // call sends none -> the caller 401s -> unauthorized event fires ->
    // AppAuthGate bounces a signed-in user back to sign-on. Force
    // credentials:'include' for all backend calls so the cookie round-trips correctly.
    //
    // ⚠️ AMENDED 2026-08-29 (R-Q15): the worked example above used to be a magic-link customer's
    // redeem setting `access_session`. That plane is deleted; the CROSS-ORIGIN mechanism, and the
    // need for this forcing, are entirely unchanged — only the cookie's name is.
    const nextInit: RequestInit = {
      ...(init ?? {}),
      headers,
      credentials: 'include',
    };
    const response = await original(input, nextInit);

    // ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15. A `handleApiResponse` call from
    // `api/accessInterceptor` stood here and classified every backend response into
    // redirected / retry / passthrough, so an ACCESS_EXPIRED / ACCESS_REVOKED / ACCESS_INVALID /
    // ACCESS_REQUIRED 401 could navigate to `/?view=access-expired` and a 503
    // VALIDATOR_UNAVAILABLE could be passed through un-notified. Every one of those codes had a
    // single producer — the magic-link access gate — and that gate, its interceptor and the
    // access-expired page are all FULL-DELETED, so the classification had exactly one outcome left
    // ('passthrough') and is removed rather than kept as a branch that can no longer be taken.
    // A 401 on the SSO-only plane is unambiguous: notify, and let the gate reconcile identity.
    if (response.status === 401) {
      notifyUnauthorized();
    }
    return response;
  };
}
